/** @jsxImportSource ../i18n */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ArrowRight, LifeBuoy, Newspaper, Sparkles } from 'lucide-react';
import { fetchPortfolio, fetchPerformance, fetchLatestNews } from '../store/slices/portfolioSlice';
import { fetchSettings } from '../store/slices/authSlice';
import { useLanguage } from '../contexts/LanguageContext';
import palette from '../theme/palette';
import { formatPercent, formatTime } from '../theme/format';
import { BrokerGate } from '../components/shell/Broker';
import { useShell } from '../components/shell/ShellContext';
import { NewsItem } from '../components/news/NewsItem';
import { PEASummaryCard } from '../components/pea/PEASummaryCard';
import { DigestCard } from '../components/reports/DigestCard';
import { TriggersCard } from '../components/reports/TriggersCard';
import { MovesCard } from '../components/reports/MovesCard';
import { WeatherCard } from '../components/stress/WeatherCard';
import { AllocationDonut, TIME_RANGES, ValueAreaChart, withColors } from '../components/charts';
import {
  Banner, Button, Card, CardHeader, Delta, Money, PageHeader, Percent, SegmentedControl, Skeleton, SkeletonList,
  SkeletonStat, Spinner, SymbolMark, Table, TBody, Td, Th, THead, Tr,
} from '../components/ui';

const ALLOCATION_SLICES = 6;

// A rough session (account down 1.5 % or more, or a drop alert) points to the
// anti-panic page before any hasty sale.
const CalmPrompt = () => {
  const { ambient } = useShell();
  const rough = ambient && ambient.pct !== null && ambient.pct !== undefined && (ambient.mood === 'storm' || (ambient.mood === 'alert' && ambient.pct < 0));
  if (!rough) return null;
  return (
    <Banner
      tone="warn"
      icon={LifeBuoy}
      data-testid="calm-prompt"
      action={<Button as={Link} to="/calme" size="sm" variant="secondary">Garder son calme</Button>}
    >
      {ambient.detail} Avant de vendre, prenez une minute pour relire votre plan.
    </Banner>
  );
};

const DashboardSkeleton = () => (
  <div className="space-y-6" role="status">
    <span className="sr-only">Chargement du portefeuille…</span>
    <Card tone="gold" padding="lg" aria-hidden="true">
      <Skeleton className="h-3 w-40" />
      <Skeleton className="mt-4 h-11 w-64" />
      <Skeleton className="mt-4 h-6 w-48" />
    </Card>
    <div className="grid gap-6 xl:grid-cols-3">
      <Card className="xl:col-span-2" aria-hidden="true"><Skeleton className="h-4 w-48" /><Skeleton className="mt-6 h-64 w-full" /></Card>
      <SkeletonStat className="h-full" />
    </div>
  </div>
);

const HeroCard = ({ summary, positionsCount }) => {
  const total = summary?.total_value || 0;
  const cash = summary?.cash_balance || 0;
  const invested = total - cash;
  const cashShare = total > 0 ? (cash / total) * 100 : 0;
  const facts = [
    { label: 'Investi', value: <Money value={invested} decimals={0} /> },
    { label: 'Liquidités', value: <Money value={cash} decimals={0} />, hint: `${formatPercent(cashShare, { sign: false })} du portefeuille` },
    { label: 'Positions', value: <span className="num">{positionsCount}</span> },
    { label: 'Rendement', value: <Percent value={summary?.total_profit_loss_pct} colorize /> },
  ];
  return (
    <Card tone="gold" padding="lg" className="animate-fade-in">
      <div className="flex flex-col gap-8 xl:flex-row xl:items-end xl:justify-between">
        <div>
          <p className="eyebrow">Valeur totale du portefeuille</p>
          <p className="mt-3 font-display text-[2.5rem] leading-[1.1] text-fg sm:text-display-xl">
            <Money value={total} decimals={0} animate />
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <Delta value={summary?.total_profit_loss} kind="money" size="md" />
            <Delta value={summary?.total_profit_loss_pct} size="md" />
            <span className="text-sm text-fg-subtle">plus-value latente</span>
          </div>
        </div>
        <dl className="grid grid-cols-2 gap-x-8 gap-y-5 sm:grid-cols-4 xl:gap-x-0 xl:divide-x xl:divide-line">
          {facts.map((fact) => (
            <div key={fact.label} className="min-w-0 xl:px-8 xl:first:pl-0 xl:last:pr-0">
              <dt className="eyebrow">{fact.label}</dt>
              <dd className="mt-2 text-xl font-semibold tracking-[-0.01em] text-fg">{fact.value}</dd>
              {fact.hint && <dd className="mt-1 text-xs text-fg-subtle">{fact.hint}</dd>}
            </div>
          ))}
        </dl>
      </div>
    </Card>
  );
};

const PerformanceCard = ({ performance, loading, range, onRange }) => {
  const data = useMemo(() => (performance?.account_value || []).map((p) => ({ date: p.date, value: p.value })), [performance]);
  let body = <ValueAreaChart data={data} height="100%" className="absolute inset-0" />;
  if (data.length === 0) {
    body = loading
      ? <Skeleton className="absolute inset-0 rounded-control" />
      : <p className="absolute inset-0 flex items-center justify-center text-sm text-fg-muted">Aucune donnée de performance sur cette période.</p>;
  }
  return (
    <Card className="flex flex-col xl:col-span-2">
      <CardHeader
        title="Évolution du portefeuille"
        description="Valorisation totale, liquidités comprises"
        actions={<SegmentedControl ariaLabel="Période du graphique" value={range} onChange={onRange} options={TIME_RANGES} />}
      />
      <div className="relative h-[220px] sm:h-[320px] xl:h-auto xl:min-h-[320px] xl:flex-1" aria-busy={loading || undefined}>
        {body}
        {loading && data.length > 0 && (
          <div className="absolute inset-0 z-10 flex items-center justify-center rounded-control bg-surface-1/60">
            <Spinner size={20} />
          </div>
        )}
      </div>
    </Card>
  );
};

const AllocationCard = ({ positions, summary }) => {
  const data = useMemo(() => {
    const sorted = [...positions].filter((p) => p.market_value > 0).sort((a, b) => b.market_value - a.market_value);
    const head = sorted.slice(0, ALLOCATION_SLICES).map((p) => ({ name: p.description || p.symbol, value: p.market_value }));
    const rest = sorted.slice(ALLOCATION_SLICES).reduce((sum, p) => sum + p.market_value, 0);
    const slices = withColors(rest > 0 ? [...head, { name: 'Autres lignes', value: rest }] : head);
    const cash = summary?.cash_balance || 0;
    return cash > 0 ? [...slices, { name: 'Liquidités', value: cash, color: palette.chart.cash }] : slices;
  }, [positions, summary]);
  return (
    <Card>
      <CardHeader title="Répartition" description="Par valorisation, liquidités comprises" />
      {data.length
        ? <AllocationDonut data={data} total={summary?.total_value} height={200} legendLimit={data.length} />
        : <p className="py-10 text-center text-sm text-fg-muted">Aucune position valorisée.</p>}
    </Card>
  );
};

const HoldingsCard = ({ positions, total, className }) => {
  const top = useMemo(() => [...positions].sort((a, b) => (b.market_value || 0) - (a.market_value || 0)).slice(0, 5), [positions]);
  const weight = (p) => (total > 0 ? ((p.market_value || 0) / total) * 100 : 0);
  return (
    <Card className={className}>
      <CardHeader
        title="Principales lignes"
        description="Les cinq premières positions par valorisation"
        actions={positions.length > 5 && (
          <Button as={Link} to="/portfolio" variant="link" trailingIcon={ArrowRight}>Voir les {positions.length} positions</Button>
        )}
      />
      <ul className="divide-y divide-line sm:hidden">
        {top.map((p) => (
          <li key={p.position_id} className="flex items-center gap-3 py-3 first:pt-0 last:pb-0">
            <SymbolMark symbol={p.symbol} />
            <Link to={`/stock/${encodeURIComponent(p.symbol)}`} className="min-w-0 flex-1">
              <span className="block font-medium text-fg">{p.symbol}</span>
              <span className="block truncate text-xs text-fg-subtle">{p.description}</span>
            </Link>
            <div className="text-right">
              <Money value={p.market_value} decimals={0} className="block font-medium text-fg" />
              <Percent value={p.profit_loss_pct} colorize className="text-xs" />
            </div>
          </li>
        ))}
      </ul>
      <Table containerClassName="hidden sm:block">
        <THead>
          <tr>
            <Th>Valeur</Th>
            <Th align="right">Valorisation</Th>
            <Th align="right" className="hidden md:table-cell">Poids</Th>
            <Th align="right">+/- value</Th>
          </tr>
        </THead>
        <TBody>
          {top.map((p) => (
            <Tr key={p.position_id}>
              <Td className="w-full max-w-0">
                <Link to={`/stock/${encodeURIComponent(p.symbol)}`} className="flex items-center gap-3">
                  <SymbolMark symbol={p.symbol} />
                  <span className="min-w-0">
                    <span className="block truncate font-medium text-fg transition-colors hover:text-gold-200">{p.symbol}</span>
                    <span className="block truncate text-xs text-fg-subtle">{p.description}</span>
                  </span>
                </Link>
              </Td>
              <Td align="right"><Money value={p.market_value} decimals={0} className="font-medium text-fg" /></Td>
              <Td align="right" className="hidden md:table-cell">
                <span className="inline-flex items-center gap-3">
                  <span aria-hidden="true" className="hidden h-1 w-16 overflow-hidden rounded-full bg-surface-3 lg:block">
                    <span className="block h-full rounded-full bg-gold-500/70" style={{ width: `${Math.min(weight(p), 100)}%` }} />
                  </span>
                  <span className="num w-12 text-fg-body">{formatPercent(weight(p), { sign: false })}</span>
                </span>
              </Td>
              <Td align="right"><Delta value={p.profit_loss_pct} /></Td>
            </Tr>
          ))}
        </TBody>
      </Table>
    </Card>
  );
};

const NewsCard = ({ latestNews, loading, generating }) => {
  const items = latestNews?.items || [];
  let status = null;
  if (generating) status = <span className="inline-flex items-center gap-2 text-warn-300"><Spinner size={12} tone="current" />Analyse en cours…</span>;
  else if (latestNews?.generated_at) status = `Mise à jour à ${formatTime(latestNews.generated_at)}`;
  return (
    <Card>
      <CardHeader
        icon={Newspaper}
        title="Actualités récentes"
        description={status}
        actions={<Button as={Link} to="/news" variant="link" trailingIcon={ArrowRight}>Tout voir</Button>}
      />
      {!generating && loading && items.length === 0 && <SkeletonList items={3} />}
      {!generating && !loading && items.length === 0 && <p className="py-8 text-center text-sm text-fg-muted">Aucune actualité marquante pour vos valeurs.</p>}
      {items.length > 0 && (
        <div className="divide-y divide-line">
          {items.slice(0, 5).map((item, i) => <NewsItem key={`${item.symbol}-${i}`} item={item} />)}
        </div>
      )}
      {generating && items.length === 0 && <SkeletonList items={3} />}
    </Card>
  );
};

const Dashboard = () => {
  const dispatch = useDispatch();
  const { summary, positions, saxoConnected, loading, error, performance, performanceLoading, latestNews, latestNewsLoading } = useSelector((state) => state.portfolio);
  const { language } = useLanguage();
  const [timeRange, setTimeRange] = useState('1m');

  useEffect(() => {
    dispatch(fetchSettings());
  }, [dispatch]);

  useEffect(() => {
    if (saxoConnected) {
      dispatch(fetchPortfolio());
      dispatch(fetchPerformance(timeRange));
    }
  }, [dispatch, saxoConnected, timeRange]);

  useEffect(() => {
    if (saxoConnected && positions.length > 0 && !latestNews && !latestNewsLoading) {
      const stocks = positions.map((p) => ({ symbol: p.symbol, description: p.description }));
      dispatch(fetchLatestNews({ stocks, language }));
    }
  }, [dispatch, saxoConnected, positions, latestNews, latestNewsLoading, language]);

  const isGenerating = latestNews?.generating === true;
  const showNews = Boolean(latestNewsLoading || latestNews);
  const pollRef = useRef(null);
  useEffect(() => {
    if (isGenerating && !latestNewsLoading && positions.length > 0) {
      pollRef.current = setInterval(() => {
        const stocks = positions.map((p) => ({ symbol: p.symbol, description: p.description }));
        dispatch(fetchLatestNews({ stocks, language }));
      }, 5000);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [isGenerating, latestNewsLoading, dispatch, positions, language]);

  return (
    <div className="space-y-8">
      <PageHeader
        eyebrow="Patrimoine"
        title="Tableau de bord"
        description="Vue d’ensemble de votre PEA."
        actions={<Button as={Link} to="/analysis" variant="secondary" leadingIcon={Sparkles}>Lancer une analyse</Button>}
      />
      <BrokerGate>
        {loading && !summary && <DashboardSkeleton />}
        {error && <Banner tone="loss" role="alert">{error}</Banner>}
        {summary && (
          <div className="space-y-6">
            <CalmPrompt />
            <HeroCard summary={summary} positionsCount={positions.length} />
            <MovesCard />
            <div className="grid gap-6 xl:grid-cols-3 xl:items-start">
              <DigestCard className="xl:col-span-2" />
              <PEASummaryCard />
            </div>
            <div className="grid gap-6 xl:grid-cols-3">
              <PerformanceCard performance={performance} loading={performanceLoading} range={timeRange} onRange={setTimeRange} />
              <AllocationCard positions={positions} summary={summary} />
            </div>
            <WeatherCard />
            <TriggersCard />
            <div className="grid gap-6 xl:grid-cols-3 xl:items-start">
              <HoldingsCard positions={positions} total={summary.total_value} className={showNews ? 'xl:col-span-2' : 'xl:col-span-3'} />
              {showNews && <NewsCard latestNews={latestNews} loading={latestNewsLoading} generating={isGenerating} />}
            </div>
          </div>
        )}
      </BrokerGate>
    </div>
  );
};

export default Dashboard;
